/**
 * GlobalUploadBar — iter805
 * ─────────────────────────────────────────────────────────────
 * Slim, always-mounted strip that floats just above the BottomNav dock
 * whenever the athlete has a video uploading/processing anywhere in the
 * app — reads from UploadManagerContext, which survives navigation, so
 * this keeps showing real progress even after switching screens.
 *
 * Hidden entirely when there's nothing in flight. Deliberately simple —
 * no per-upload list, just the most relevant one (still sending bytes
 * takes priority over one just waiting on Mux) plus a "+N more" count.
 *
 * iter806 · Jay: a video that's still "processing" can sit there a
 * while (waiting on Mux/the webhook) — collapsible so it doesn't block
 * the view if someone just wants to use the rest of the app. Collapsing
 * only dismisses the CURRENT batch: if a genuinely new upload starts
 * afterward (a mux_upload_id the collapse didn't know about), the bar
 * re-expands on its own rather than staying hidden for something the
 * athlete never actually saw.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useUploadManager } from '../contexts/UploadManagerContext';
const GlobalUploadBar = () => {
  const { activeUploads } = useUploadManager();
  const [collapsed, setCollapsed] = useState(false);
  const collapsedIdsRef = useRef(new Set());
  const currentIds = new Set(activeUploads.map((u) => u.muxUploadId).filter(Boolean));
  useEffect(() => {
    if (!collapsed) return;
    // Any id present now that wasn't there when we collapsed → a new
    // upload the athlete hasn't seen the bar for yet. Re-expand for it.
    for (const id of currentIds) {
      if (!collapsedIdsRef.current.has(id)) {
        setCollapsed(false);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUploads, collapsed]);
  if (!activeUploads.length) return null;
  // iter817 · A failed upload takes priority over a merely-processing
  // one — Jay: this used to just vanish with no signal at all once the
  // backend confirmed it had actually died, which read as "did it work
  // or not?" A real failure should be the loudest thing in the bar, not
  // silently dropped.
  const primary = activeUploads.find((u) => u.status === 'failed')
    || activeUploads.find((u) => u.status === 'uploading')
    || activeUploads[0];
  const extra = activeUploads.length - 1;
  const isFailed = primary.status === 'failed';
  const label = isFailed
    ? `Upload failed for ${primary.eventTitle} — try again`
    : primary.status === 'uploading'
      ? `Uploading to ${primary.eventTitle}… ${primary.progress}%`
      : `Processing video for ${primary.eventTitle}…`;
  const onCollapse = () => {
    collapsedIdsRef.current = currentIds;
    setCollapsed(true);
  };
  return (
    <div
      className="fixed inset-x-0 z-[65] flex justify-center px-4 sm:px-6"
      style={{
        // Sits just above the BottomNav capsule (which itself floats
        // `env(safe-area-inset-bottom) + 8px` up) — same horizontal
        // inset as the nav so the two feel like one stacked unit.
        bottom: 'calc(env(safe-area-inset-bottom) + 76px)',
      }}
      data-testid="global-upload-bar"
    >
      {collapsed ? (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="pointer-events-auto flex items-center gap-1.5 rounded-full backdrop-blur-[28px] backdrop-saturate-150 bg-black/70 ring-1 ring-white/15 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55)] px-3 py-1.5"
          data-testid="global-upload-bar-collapsed"
          aria-label="Show upload progress"
        >
          <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${isFailed ? 'bg-red-400' : 'bg-emerald-400'}`} aria-hidden="true" />
          <span className="text-[10px] font-bold text-white/80 uppercase tracking-wide">{isFailed ? 'Failed' : 'Uploading'}</span>
        </button>
      ) : (
        <div className={`pointer-events-auto w-full max-w-md rounded-2xl backdrop-blur-[28px] backdrop-saturate-150 bg-black/70 ring-1 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55)] px-4 py-2.5 ${isFailed ? 'ring-red-500/40' : 'ring-white/15'}`}>
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[11px] font-bold truncate ${isFailed ? 'text-red-200' : 'text-white'}`} data-testid="global-upload-bar-label">
              {label}{extra > 0 ? ` · +${extra} more` : ''}
            </span>
            <button
              type="button"
              onClick={onCollapse}
              className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white/60 hover:text-white/90 hover:bg-white/10 transition"
              data-testid="global-upload-bar-collapse"
              aria-label="Hide upload progress"
            >
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
          </div>
          <div className="mt-1.5 h-1 rounded-full bg-white/15 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ease-out ${isFailed ? 'bg-red-400' : 'bg-emerald-400'} ${primary.status !== 'uploading' ? 'animate-pulse' : ''}`}
              style={{ width: primary.status === 'uploading' ? `${primary.progress}%` : '100%' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
export default GlobalUploadBar;
